import React, { useRef, useState } from 'react';
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetError,
} from 'react-hook-form';
import { TextField, Typography, Box, Modal, IconButton } from '@mui/material';
import { imgUrl } from '../../Helpers/utils/Constant';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';

type IProps = {
  isRequired?: boolean;
  register: UseFormRegister<any>;
  clearErrors: UseFormClearErrors<any>;
  errors: FieldErrors<any>;
  label: string;
  fieldName: string;
  setError: UseFormSetError<any>;
  onChange?: (files: File[]) => void;
  mt?: '0';
  hideCustomLabel?: boolean;
  onlyPdf?: boolean;
  onlyImage?: boolean;
  fileLimitSize?: number;
  imgSerUrl?: string[] | undefined;
  removeExistingImaFun?: (exitRemoveImgList: string[]) => void;
  addNewImagesFun?: (addNewImages: (string | File)[]) => void;
  mode: 'single' | 'multiple';
};

const MultipleAndSingleImgValidateWithPreview = ({
  isRequired,
  register,
  clearErrors,
  errors,
  label,
  fieldName,
  setError,
  onChange,
  mt,
  hideCustomLabel,
  onlyImage,
  onlyPdf,
  fileLimitSize,
  imgSerUrl,
  removeExistingImaFun,
  mode,
  addNewImagesFun,
}: IProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [images, setImages] = useState<(File | string)[]>(
    imgSerUrl && imgSerUrl?.length ? [...imgSerUrl] : []
  );
  const [newImages, setNewImages] = useState<(File | string)[]>([]);
  const [open, setOpen] = useState(false);
  const [modalImage, setModalImage] = useState<string | null>(null);

  const validateFile = (fileList: FileList | undefined) => {
    const fileSize = fileLimitSize;
    const file = fileList?.[0];

    if (file && file.size > (fileSize || 5) * 1024 * 1024) {
      return `File size exceeds ${fileSize || 5} MB.`;
    }
    return true;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const fileArray = Array.from(files);
      const validFiles = fileArray.filter(
        (file) => file.size <= (fileLimitSize || 5) * 1024 * 1024
      );

      if (validFiles.length !== fileArray.length) {
        setError(fieldName, {
          type: 'validate',
          message: `Some files exceed the size limit of ${
            fileLimitSize || 5
          } MB.`,
        });
      } else {
        clearErrors(fieldName);
      }
      /* ---------mode------------------------ */
      if (mode === 'single') {
        setNewImages([...validFiles]);
        setImages([...validFiles]);
      } else {
        setNewImages((prevImages) => {
          addNewImagesFun && addNewImagesFun([...prevImages, ...validFiles]);
          return [...prevImages, ...validFiles];
        });
        setImages((prevImages) => [...prevImages, ...validFiles]);
      }

      onChange && onChange(validFiles);
    }
  };
  const [removeExistingImage, setExistingImage] = useState<string[]>([]);
  const removeImage = (index: number) => {
    setImages((prevImages) => {
      const removeImage = prevImages[index];
      console.log(removeImage, index);
      if (mode === 'multiple') {
        // multiple mode can delete existing images
        setExistingImage((prev: any) => {
          removeExistingImaFun && removeExistingImaFun([...prev, removeImage]);
          return [...prev, removeImage];
        });
      }

      return prevImages?.filter((_, i) => i !== index);
    });
    if (inputRef.current) {
      inputRef.current.value = ''; // Reset the file input
    }
  };
  const handleOpenModal = (image: string) => {
    setModalImage(image);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setModalImage(null);
  };
  return (
    <Box sx={{ mt: mt ? 0 : 2 }}>
      {!hideCustomLabel && (
        <label>
          {label} {isRequired && <span style={{ color: 'red' }}>*</span>}
        </label>
      )}
      <TextField
        inputRef={inputRef}
        id={`file-input-${fieldName}`}
        variant='outlined'
        type='file'
        label={
          hideCustomLabel ? (
            <>
              {label}
              {isRequired && <span style={{ color: 'red' }}> *</span>}
            </>
          ) : (
            ''
          )
        }
        InputLabelProps={{
          shrink: true,
        }}
        InputProps={{
          inputProps: {
            accept: onlyImage
              ? 'image/jpeg,image/png,image/jpg'
              : onlyPdf
              ? 'application/pdf'
              : '',
            multiple: true,
          },
        }}
        fullWidth
        {...register(fieldName, {
          validate: validateFile,
          ...(isRequired && { required: 'File is required.' }),
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            handleFileChange(e);
            if (file && file.size <= (fileLimitSize || 5) * 1024 * 1024) {
              // onChange && onChange(file);
              clearErrors(fieldName);
            } else {
              setError(fieldName, {
                type: 'validate',
                message: `File size exceeds ${fileLimitSize || 5} MB.`,
              });
            }
          },
        })}
      />
      {errors[fieldName] && (
        <Typography color='error' variant='body2'>
          {errors[fieldName]?.message as string}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: '2px' }}>
        {images?.map((image: any, index) => {
          const isPdf =
            image?.type === 'application/pdf' ||
            (typeof image === 'string' && image.endsWith('.pdf'));

          const imageUrl =
            image && typeof image === 'string'
              ? `${imgUrl}/${image}`
              : image
              ? URL.createObjectURL(image)
              : '';

          return (
            <Box
              key={index}
              sx={{
                position: 'relative',
                width: 40,
                height: 40,
                border: '1px solid #ccc',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              {isPdf ? (
                <>
                  <span
                    style={{ position: 'absolute', top: '10px', right: '7px' }}
                  >
                    PDF
                  </span>
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 3,
                      width: '10px',
                      height: '10px',
                      background: 'rgba(0,0,0,0.5)',
                      color: 'white',
                    }}
                    onClick={() => removeImage(index)}
                  >
                    <DeleteIcon sx={{ fontSize: '12px' }} />
                  </IconButton>
                </>
              ) : (
                <>
                  <img
                    src={imageUrl}
                    alt={`Preview ${index}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    onClick={() => handleOpenModal(imageUrl)}
                  />
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 3,
                      width: '10px',
                      height: '10px',
                      background: 'rgba(0,0,0,0.5)',
                      color: 'white',
                    }}
                    onClick={() => removeImage(index)}
                  >
                    <DeleteIcon sx={{ fontSize: '12px' }} />
                  </IconButton>
                </>
              )}
            </Box>
          );
        })}
      </Box>

      <Modal open={open} onClose={handleCloseModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 2,
            maxWidth: '90%',
            maxHeight: '90%',
            overflow: 'auto',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'end' }}>
            <CloseIcon sx={{ cursor: 'pointer' }} onClick={handleCloseModal} />
          </Box>
          {modalImage && (
            <img
              src={modalImage}
              alt='Full-size preview'
              style={{ width: '100%', height: 'auto' }}
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default MultipleAndSingleImgValidateWithPreview;
